import _regeneratorRuntime from "/data/home/fjc/workspace/tfjs-yolov5-example/node_modules/babel-preset-react-app/node_modules/@babel/runtime/regenerator";
import _asyncToGenerator from "/data/home/fjc/workspace/tfjs-yolov5-example/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator";

/**
 * @license
 * Copyright 2018 Google LLC
 *
 * Use of this source code is governed by an MIT-style
 * license that can be found in the LICENSE file or at
 * https://opensource.org/licenses/MIT.
 * =============================================================================
 */

/**
 * Interfaces and methods for training models using tf.Tensor objects.
 */
import * as tfc from '@tensorflow/tfjs-core';
import { Tensor, tensor1d, util } from '@tensorflow/tfjs-core';
import { expandDims, gather, sliceAlongFirstAxis } from '../backend/tfjs_backend';
import { configureCallbacks, standardizeCallbacks } from '../base_callbacks';
import { NotImplementedError, ValueError } from '../errors';
import { disposeTensorsInLogs } from '../logs';
import { range } from '../utils/math_utils';
export function checkBatchSize(batchSize) {
  tfc.util.assert(batchSize > 0 && Number.isInteger(batchSize), function () {
    return "batchSize is required to be a positive integer, but got ".concat(batchSize);
  });
}
/**
 * Slice a Tensor or an Array of Tensors, by start and stop indices.
 *
 * Porting Note: The `_slice_arrays` function in PyKeras is covered by this
 *   function and `sliceArraysByIndices()` together.
 *
 * @param arrays: the input.
 * @param start: the starting index (inclusive).
 * @param stop: the stopping index (exclusive).
 * @returns The result of the slicing. If `arrays` is an `Array` of
 *   `tf.Tensor`s, the slicing will be applied to all elements of the `Array`
 *   in the same way.
 */

export function sliceArrays(arrays, start, stop) {
  if (arrays == null) {
    return [null];
  } else if (Array.isArray(arrays)) {
    return arrays.map(function (array) {
      return sliceAlongFirstAxis(array, start, stop - start);
    });
  } else {
    // Tensor.
    return sliceAlongFirstAxis(arrays, start, stop - start);
  }
}
/**
 * Slice a Tensor or an Array of Tensors, by random-order indices.
 *
 * Porting Note: The `_slice_arrays` function in PyKeras is covered by this
 *   function and `sliceArrays()` together.
 *
 * @param arrays The input `tf.Tensor` or `Array` of `tf.Tensor`s to slice.
 *   If an `Array` of `tf.Tensor`s, all `tf.Tensor`s will be sliced in the
 *   same fashion.
 * @param indices The indices to use for slicing along the first (batch)
 *   dimension.
 * @returns Result(s) of the slicing.
 */

export function sliceArraysByIndices(arrays, indices) {
  return tfc.tidy(function () {
    if (arrays == null) {
      return null;
    } else if (Array.isArray(arrays)) {
      return arrays.map(function (array) {
        return sliceArraysByIndices(array, indices);
      });
    } else {
      // TODO(cais): indices should be a pre-constructed Tensor1D to avoid
      //   tensor1d() calls.
      return gather(arrays, indices.dtype === 'int32' ? indices : indices.toInt());
    }
  });
}
/**
 * Returns a list of batch indices (tuples of indices).
 * @param size: Integer, total size of the data to slice into batches.
 * @param batchSize: Integer, batch size.
 * @returns An Array of [batchStart, batchEnd] tuples. batchStart is
 *   inclusive; batchEnd is exclusive. I.e., each batch consists of indices x
 *   that satisfy batchStart <= x < batchEnd.
 */

export function makeBatches(size, batchSize) {
  var output = [];
  var batchStart = 0;
  var batchEnd = null;

  while (batchStart < size) {
    batchEnd = batchStart + batchSize;

    if (batchEnd >= size) {
      batchEnd = size;
    }

    output.push([batchStart, batchEnd]);
    batchStart = batchEnd;
  }

  return output;
}
/**
 * Abstract fit function for `f(ins)`.
 * @param f A Function returning a list of tensors. For training, this
 *   function is expected to perform the updates to the variables.
 * @param ins List of tensors to be fed to `f`.
 * @param outLabels List of strings, display names of the outputs of `f`.
 * @param batchSize Integer batch size or `== null` if unknown. Default : 32.
 * @param epochs Number of times to iterate over the data. Default : 1.
 * @param verbose Verbosity mode: 0, 1, or 2. Default: 1.
 * @param callbacks List of callbacks to be called during training.
 * @param valF Function to call for validation.
 * @param valIns List of tensors to be fed to `valF`.
 * @param shuffle Whether to shuffle the data at the beginning of every
 * epoch. Default : true.
 * @param callbackMetrics List of strings, the display names of the metrics
 *   passed to the callbacks. They should be the concatenation of the
 *   display names of the outputs of `f` and the list of display names
 *   of the outputs of `valF`.
 * @param initialEpoch Epoch at which to start training (useful for
 *   resuming a previous training run). Default : 0.
 * @param stepsPerEpoch Total number of steps (batches on samples) before
 *   declaring one epoch finished and starting the next epoch. Ignored with
 *   the default value of `undefined` or `null`.
 * @param validationSteps Number of steps to run validation for (only if
 *   doing validation from data tensors). Not applicable for tfjs-layers.
 * @returns A `History` object.
 */

function fitLoop(_x, _x2, _x3, _x4, _x5, _x6, _x7, _x8, _x9, _x10, _x11, _x12, _x13, _x14, _x15) {
  return _fitLoop.apply(this, arguments);
}

function _fitLoop() {
  _fitLoop = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2( // Type `model` as `any` here to avoid circular dependency w/ training.ts.
  // tslint:disable-next-line:no-any
  model, f, ins, outLabels, batchSize, epochs, verbose, callbacks, valF, valIns, shuffle, callbackMetrics, initialEpoch, stepsPerEpoch, validationSteps) {
    var doValidation, numTrainSamples, indexArray, _configureCallbacks, callbackList, history, _loop, epoch, _ret;

    return _regeneratorRuntime.wrap(function _callee2$(_context4) {
      while (1) {
        switch (_context4.prev = _context4.next) {
          case 0:
            if (batchSize == null) {
              batchSize = 32;
            }

            if (epochs == null) {
              epochs = 1;
            }

            if (shuffle == null) {
              shuffle = true;
            }

            if (initialEpoch == null) {
              initialEpoch = 0;
            } // TODO(cais): Change const to let below when implementing validation.


            doValidation = false;

            if (valF != null && valIns != null) {
              doValidation = true; // TODO(cais): verbose message.
            }

            if (!(validationSteps != null)) {
              _context4.next = 10;
              break;
            }

            doValidation = true;

            if (!(stepsPerEpoch == null)) {
              _context4.next = 10;
              break;
            }

            throw new ValueError('Can only use `validationSteps` when doing step-wise training, ' + 'i.e., `stepsPerEpoch` must be set.');

          case 10:
            numTrainSamples = model.checkNumSamples(ins, batchSize, stepsPerEpoch, 'steps_per_epoch');

            if (numTrainSamples != null) {
              indexArray = range(0, numTrainSamples);
            }

            if (verbose == null) {
              verbose = 1;
            }

            _configureCallbacks = configureCallbacks(callbacks, verbose, epochs, initialEpoch, numTrainSamples, stepsPerEpoch, batchSize, doValidation, callbackMetrics), callbackList = _configureCallbacks.callbackList, history = _configureCallbacks.history;
            callbackList.setModel(model);
            model.history = history;
            _context4.next = 18;
            return callbackList.onTrainBegin();

          case 18:
            model.stopTraining_ = false; // TODO(cais): Take care of callbacks.validation_data as in PyKeras.
            // TODO(cais): Pre-convert feeds for performance as in PyKeras.

            _loop = /*#__PURE__*/_regeneratorRuntime.mark(function _loop(epoch) {
              var epochLogs;
              return _regeneratorRuntime.wrap(function _loop$(_context3) {
                while (1) {
                  switch (_context3.prev = _context3.next) {
                    case 0:
                      _context3.next = 2;
                      return callbackList.onEpochBegin(epoch);

                    case 2:
                      epochLogs = {};

                      if (!(stepsPerEpoch != null)) {
                        _context3.next = 7;
                        break;
                      }

                      throw new NotImplementedError('stepsPerEpoch mode is not implemented yet.');

                    case 7:
                      return _context3.delegateYield( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
                        var epochIndexArray1D, batches, _loop2, batchIndex, _ret2;

                        return _regeneratorRuntime.wrap(function _callee$(_context2) {
                          while (1) {
                            switch (_context2.prev = _context2.next) {
                              case 0:
                                if (!(shuffle === 'batch')) {
                                  _context2.next = 4;
                                  break;
                                }

                                throw new NotImplementedError('batch shuffling is not implemneted yet');

                              case 4:
                                if (shuffle) {
                                  util.shuffle(indexArray);
                                }

                              case 5:
                                // Convert the potentially shuffled indices to Tensor1D, to avoid the
                                // cost of repeated creation of Array1Ds later on.
                                epochIndexArray1D = tensor1d(indexArray);
                                batches = makeBatches(numTrainSamples, batchSize);
                                _loop2 = /*#__PURE__*/_regeneratorRuntime.mark(function _loop2(batchIndex) {
                                  var batchLogs;
                                  return _regeneratorRuntime.wrap(function _loop2$(_context) {
                                    while (1) {
                                      switch (_context.prev = _context.next) {
                                        case 0:
                                          batchLogs = {};
                                          _context.next = 3;
                                          return callbackList.onBatchBegin(batchIndex, batchLogs);

                                        case 3:
                                          tfc.tidy(function () {
                                            var batchStart = batches[batchIndex][0];
                                            var batchEnd = batches[batchIndex][1];
                                            var batchIds = sliceAlongFirstAxis(epochIndexArray1D, batchStart, batchEnd - batchStart);
                                            batchLogs['batch'] = batchIndex;
                                            batchLogs['size'] = batchEnd - batchStart; // TODO(cais): In ins, train flag can be a number, instead of an
                                            //   Tensor? Do we need to handle this in tfjs-layers?

                                            var insBatch = sliceArraysByIndices(ins, batchIds);
                                            var outs = f(insBatch);

                                            for (var i = 0; i < outLabels.length; ++i) {
                                              var label = outLabels[i];
                                              var out = outs[i];
                                              batchLogs[label] = out;
                                              tfc.keep(out); // TODO(cais): Use scope() to avoid ownership.
                                            }

                                            if (batchIndex === batches.length - 1) {
                                              // Last batch.
                                              if (doValidation) {
                                                var valOuts = model.testLoop(valF, valIns, batchSize); // Porting Notes: In tfjs-layers, valOuts is always an Array.

                                                for (var _i = 0; _i < outLabels.length; ++_i) {
                                                  var _label = outLabels[_i];
                                                  var _out = valOuts[_i];
                                                  tfc.keep(_out); // TODO(cais): Use scope() to avoid ownership.

                                                  epochLogs['val_' + _label] = _out;
                                                }
                                              }
                                            }
                                          });
                                          _context.next = 6;
                                          return callbackList.onBatchEnd(batchIndex, batchLogs);

                                        case 6:
                                          disposeTensorsInLogs(batchLogs);

                                          if (!model.stopTraining_) {
                                            _context.next = 9;
                                            break;
                                          }

                                          return _context.abrupt("return", "break");

                                        case 9:
                                        case "end":
                                          return _context.stop();
                                      }
                                    }
                                  }, _loop2);
                                });
                                batchIndex = 0;

                              case 9:
                                if (!(batchIndex < batches.length)) {
                                  _context2.next = 17;
                                  break;
                                }

                                return _context2.delegateYield(_loop2(batchIndex), "t0", 11);

                              case 11:
                                _ret2 = _context2.t0;

                                if (!(_ret2 === "break")) {
                                  _context2.next = 14;
                                  break;
                                }

                                return _context2.abrupt("break", 17);

                              case 14:
                                ++batchIndex;
                                _context2.next = 9;
                                break;

                              case 17:
                                epochIndexArray1D.dispose();

                              case 18:
                              case "end":
                                return _context2.stop();
                            }
                          }
                        }, _callee);
                      })(), "t0", 8);

                    case 8:
                      _context3.next = 10;
                      return callbackList.onEpochEnd(epoch, epochLogs);

                    case 10:
                      if (!model.stopTraining_) {
                        _context3.next = 12;
                        break;
                      }

                      return _context3.abrupt("return", "break");

                    case 12:
                    case "end":
                      return _context3.stop();
                  }
                }
              }, _loop);
            });
            epoch = initialEpoch;

          case 21:
            if (!(epoch < epochs)) {
              _context4.next = 29;
              break;
            }

            return _context4.delegateYield(_loop(epoch), "t0", 23);

          case 23:
            _ret = _context4.t0;

            if (!(_ret === "break")) {
              _context4.next = 26;
              break;
            }

            return _context4.abrupt("break", 29);

          case 26:
            ++epoch;
            _context4.next = 21;
            break;

          case 29:
            _context4.next = 31;
            return callbackList.onTrainEnd();

          case 31:
            _context4.next = 33;
            return model.history.syncData();

          case 33:
            return _context4.abrupt("return", model.history);

          case 34:
          case "end":
            return _context4.stop();
        }
      }
    }, _callee2);
  }));
  return _fitLoop.apply(this, arguments);
}

export function fitTensors(_x16, _x17, _x18) {
  return _fitTensors.apply(this, arguments);
}
/**
 * Ensure tensors all have a rank of at least 2.
 *
 * If a tensor has a rank of 1, it is dimension-expanded to rank 2.
 * If any tensor has a rank of 0 (i.e., is a scalar), an error will be thrown.
 */

function _fitTensors() {
  _fitTensors = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3( // Type `model` as `any` here to avoid circular dependency w/ training.ts.
  // tslint:disable-next-line:no-any
  model, x, y) {
    var args,
        inputs,
        targets,
        inputValX,
        inputValY,
        valX,
        valY,
        sampleWeights,
        batchSize,
        checkBatchAxis,
        standardizedOuts,
        doValidation,
        valIns,
        _checkBatchAxis,
        valStandardized,
        splitAt,
        originalBatchSize,
        ins,
        trainFunction,
        outLabels,
        valFunction,
        callbackMetrics,
        callbacks,
        out,
        _args5 = arguments;

    return _regeneratorRuntime.wrap(function _callee3$(_context5) {
      while (1) {
        switch (_context5.prev = _context5.next) {
          case 0:
            args = _args5.length > 3 && _args5[3] !== undefined ? _args5[3] : {};

            if (!model.isTraining) {
              _context5.next = 3;
              break;
            }

            throw new Error('Cannot start training because another fit() call is ongoing.');

          case 3:
            model.isTraining = true;
            _context5.prev = 4;
            batchSize = args.batchSize == null ? 32 : args.batchSize;
            checkBatchSize(batchSize); // Validate user data.
            // TODO(cais): Support sampleWeight.

            checkBatchAxis = false;
            _context5.next = 10;
            return model.standardizeUserData(x, y, args.sampleWeight, args.classWeight, checkBatchAxis, batchSize);

          case 10:
            standardizedOuts = _context5.sent;
            inputs = standardizedOuts[0];
            targets = standardizedOuts[1];
            sampleWeights = standardizedOuts[2]; // Prepare validation data.

            doValidation = false;

            if (!(args.validationData != null && args.validationData.length > 0)) {
              _context5.next = 36;
              break;
            }

            doValidation = true;

            if (!(args.validationData.length === 2)) {
              _context5.next = 22;
              break;
            }

            // config.validationData consists of valX and valY.
            inputValX = args.validationData[0];
            inputValY = args.validationData[1];
            _context5.next = 27;
            break;

          case 22:
            if (!(args.validationData.length === 3)) {
              _context5.next = 26;
              break;
            }

            throw new NotImplementedError('validationData including sample weights is not supported yet.');

          case 26:
            throw new ValueError("When passing validation data, it must contain 2 (valX, valY) " + "or 3 (valX, valY, valSampleWeight) items; " + "".concat(args.validationData, " is invalid."));

          case 27:
            _checkBatchAxis = true;
            _context5.next = 30;
            return model.standardizeUserData(inputValX, inputValY, null,
            /** Unused sample weights. */
            null,
            /** Unused class weights. */
            _checkBatchAxis, batchSize);

          case 30:
            valStandardized = _context5.sent;
            valX = valStandardized[0];
            valY = valStandardized[1];
            valIns = valX.concat(valY); // TODO(cais): Add useLearningPhase data properly.

            _context5.next = 37;
            break;

          case 36:
            if (args.validationSplit != null && args.validationSplit > 0 && args.validationSplit < 1) {
              doValidation = true; // Porting Note: In tfjs-layers, inputs[0] is always a Tensor.

              splitAt = Math.floor(inputs[0].shape[0] * (1 - args.validationSplit));
              originalBatchSize = inputs[0].shape[0];
              valX = sliceArrays(inputs, splitAt, originalBatchSize);
              inputs = sliceArrays(inputs, 0, splitAt);
              valY = sliceArrays(targets, splitAt, originalBatchSize);
              targets = sliceArrays(targets, 0, splitAt); // TODO(cais): Once sampleWeights becomes available, slice it to get
              //   valSampleWeights.

              valIns = valX.concat(valY); // TODO(cais): Add useLearningPhase data properly.
            } else if (args.validationSteps != null) {
              doValidation = true; // TODO(cais): Add useLearningPhase.
            }

          case 37:
            ins = inputs.concat(targets).concat(sampleWeights);
            model.checkTrainableWeightsConsistency(); // TODO(cais): Handle use_learning_phase and learning_phase?
            // Porting Note: Here we see a key deviation of tfjs-layers from
            // Keras.
            //  Due to the imperative nature of tfjs-layers' backend (tfjs-core),
            //  we do not construct symbolic computation graphs to embody the
            //  training process. Instead, we define a function that performs the
            //  training action. In PyKeras, the data (inputs and targets) are fed
            //  through graph placeholders. In tfjs-layers, the data are fed as
            //  function arguments. Since the function are defined below in the
            //  scope, we don't have equivalents of PyKeras's
            //  `_make_train_funciton`.

            trainFunction = model.makeTrainFunction();
            outLabels = model.getDedupedMetricsNames();

            if (doValidation) {
              model.makeTestFunction();
              valFunction = model.testFunction;
              callbackMetrics = outLabels.slice().concat(outLabels.map(function (n) {
                return 'val_' + n;
              }));
            } else {
              valFunction = null;
              valIns = [];
              callbackMetrics = outLabels.slice();
            }

            callbacks = standardizeCallbacks(args.callbacks, args.yieldEvery);
            _context5.next = 45;
            return fitLoop(model, trainFunction, ins, outLabels, batchSize, args.epochs, args.verbose, callbacks, valFunction, valIns, args.shuffle, callbackMetrics, args.initialEpoch, null, null);

          case 45:
            out = _context5.sent;
            return _context5.abrupt("return", out);

          case 47:
            _context5.prev = 47;
            model.isTraining = false; // Memory clean up.

            disposeNewTensors(inputs, x);
            disposeNewTensors(targets, y);
            disposeNewTensors(valX, inputValX);
            disposeNewTensors(valY, inputValY);

            if (sampleWeights != null) {
              tfc.dispose(sampleWeights);
            }

            return _context5.finish(47);

          case 55:
          case "end":
            return _context5.stop();
        }
      }
    }, _callee3, null, [[4,, 47, 55]]);
  }));
  return _fitTensors.apply(this, arguments);
}

export function ensureTensorsRank2OrHigher(tensors) {
  var outs = [];

  if (tensors instanceof Tensor) {
    tensors = [tensors];
  } // Make Tensors at least 2D.


  for (var i = 0; i < tensors.length; ++i) {
    var tensor = tensors[i];

    if (tensor.rank === 1) {
      outs.push(expandDims(tensor, 1));
    } else if (tensor.rank === 0) {
      throw new Error('Expected tensor to be at least 1D, but received a 0D tensor ' + '(scalar).');
    } else {
      outs.push(tensor);
    }
  }

  return outs;
}
/**
 * Compare a set of tensors with a reference (old) set, discard the ones
 * in the new set that are not present in the reference set.
 *
 * This method is used for memory clenaup during calls such as
 * LayersModel.fit().
 *
 * @param tensors New set which may contain Tensors not present in
 *   `refTensors`.
 * @param refTensors Reference Tensor set.
 */
// TODO(cais, kangyizhang): Deduplicate with tfjs-data.

export function disposeNewTensors(tensors, refTensors) {
  if (tensors == null) {
    return;
  }

  var oldTensorIds = [];

  if (refTensors instanceof Tensor) {
    oldTensorIds.push(refTensors.id);
  } else if (Array.isArray(refTensors)) {
    refTensors.forEach(function (t) {
      return oldTensorIds.push(t.id);
    });
  } else if (refTensors != null) {
    // `oldTensors` is a map from string name to Tensor.
    for (var name in refTensors) {
      var oldTensor = refTensors[name];
      oldTensorIds.push(oldTensor.id);
    }
  }

  var tensorsToDispose = [];

  if (tensors instanceof Tensor) {
    if (oldTensorIds.indexOf(tensors.id) === -1) {
      tensorsToDispose.push(tensors);
    }
  } else if (Array.isArray(tensors)) {
    tensors.forEach(function (t) {
      if (oldTensorIds.indexOf(t.id) === -1) {
        tensorsToDispose.push(t);
      }
    });
  } else if (tensors != null) {
    // `oldTensors` is a map from string name to Tensor.
    for (var _name in tensors) {
      var tensor = tensors[_name];

      if (oldTensorIds.indexOf(tensor.id) === -1) {
        tensorsToDispose.push(tensor);
      }
    }
  }

  tensorsToDispose.forEach(function (t) {
    if (!t.isDisposed) {
      t.dispose();
    }
  });
}